import { type RefObject, useEffect, useRef } from "react";
import { hasEnteredView } from "../lib/entrance-reveal";

// On its first appearance a light fades in while its tail stretches out
// behind the head, after a short pause for the page to settle.
const ENTRANCE_SECONDS = 1.6;
const ENTRANCE_PAUSE_SECONDS = 0.3;

type Rgb = readonly [number, number, number];

interface Stack {
  colour: Rgb;
  /** Opacity of each stacked dash. */
  alpha: number;
  layers: number;
  /** Share of the perimeter (0-100) behind and ahead of the brightest point. */
  back: number;
  front: number;
}

const VIOLET: Rgb = [178, 150, 255];
const BLUE: Rgb = [110, 160, 255];

// A long lavender arc with a shorter blue band on top near the front.
const ARC: readonly Stack[] = [
  { colour: VIOLET, alpha: 0.13, layers: 16, back: 42, front: 8 },
  { colour: BLUE, alpha: 0.16, layers: 16, back: 18, front: 6 }
];

const PALE_ROSE: Rgb = [240, 158, 194];
const ROSE: Rgb = [226, 104, 158];

// A pale rose tail with a deeper rose band near the front.
const ROSE_TRAIL: readonly Stack[] = [
  { colour: PALE_ROSE, alpha: 0.14, layers: 16, back: 22, front: 3 },
  { colour: ROSE, alpha: 0.17, layers: 16, back: 9, front: 2 }
];

/**
 * A rounded border's centre line as a polyline, traced clockwise from the end
 * of the top-left corner, with the running length at every vertex.
 */
function borderPolyline(x: number, y: number, width: number, height: number, radius: number) {
  const r = Math.min(radius, width / 2, height / 2);
  const points: [number, number][] = [];
  const corner = (cx: number, cy: number, from: number) => {
    for (let step = 0; step <= 12; step++) {
      const angle = from + (step / 12) * (Math.PI / 2);
      points.push([cx + r * Math.cos(angle), cy + r * Math.sin(angle)]);
    }
  };
  points.push([x + r, y]);
  corner(x + width - r, y + r, -Math.PI / 2);
  corner(x + width - r, y + height - r, 0);
  corner(x + r, y + height - r, Math.PI / 2);
  corner(x + r, y + r, Math.PI);
  const lengths = [0];
  for (let index = 1; index < points.length; index++) {
    const [ax, ay] = points[index - 1];
    const [bx, by] = points[index];
    lengths.push(lengths[index - 1] + Math.hypot(bx - ax, by - ay));
  }
  return { points, lengths, perimeter: lengths[lengths.length - 1] };
}

type Polyline = ReturnType<typeof borderPolyline>;

function pointAt({ points, lengths }: Polyline, distance: number): [number, number] {
  let low = 0;
  let high = lengths.length - 1;
  while (high - low > 1) {
    const middle = (low + high) >> 1;
    if (lengths[middle] <= distance) low = middle;
    else high = middle;
  }
  const span = lengths[high] - lengths[low] || 1;
  const t = (distance - lengths[low]) / span;
  const [ax, ay] = points[low];
  const [bx, by] = points[high];
  return [ax + (bx - ax) * t, ay + (by - ay) * t];
}

/** Adds the stretch of border from `start` to `end` (CSS px, may wrap) to the path. */
function traceRange(context: CanvasRenderingContext2D, line: Polyline, start: number, end: number) {
  const { perimeter, points, lengths } = line;
  const from = ((start % perimeter) + perimeter) % perimeter;
  let remaining = end - start;
  let at = from;
  while (remaining > 0) {
    const stop = Math.min(perimeter, at + remaining);
    context.moveTo(...pointAt(line, at));
    for (let index = 0; index < lengths.length; index++) {
      if (lengths[index] > at && lengths[index] < stop) context.lineTo(...points[index]);
    }
    context.lineTo(...pointAt(line, stop));
    remaining -= stop - at;
    at = 0;
  }
}

interface Segment {
  /** Start and end as a share of the perimeter (0-100) at progress zero. */
  from: number;
  to: number;
  style: string;
}

/**
 * One band of light is a set of stacked dashes that all contain the brightest
 * point and reach a little further each, so brightness falls off
 * continuously behind it and, more steeply, ahead of it. Instead of painting
 * every dash over the others each frame, this works out once what the stack
 * composites to between consecutive dash ends and returns those stretches,
 * so a frame paints each point of the band once.
 */
function bandSegments(stacks: readonly Stack[]): Segment[] {
  const edges = new Set<number>();
  for (const { layers, back, front } of stacks) {
    for (let layer = 1; layer <= layers; layer++) {
      edges.add(100 - front - (back * layer) / layers);
      edges.add(100 - front + (front * layer) / layers);
    }
  }
  const sorted = [...edges].sort((a, b) => a - b);
  const segments: Segment[] = [];
  for (let index = 1; index < sorted.length; index++) {
    const from = sorted[index - 1];
    const to = sorted[index];
    const middle = (from + to) / 2;
    // Composite each stack over the ones before it, premultiplied.
    let red = 0;
    let green = 0;
    let blue = 0;
    let opacity = 0;
    for (const { colour, alpha, layers, back, front } of stacks) {
      const peak = 100 - front;
      let count = 0;
      for (let layer = 1; layer <= layers; layer++) {
        if (middle >= peak - (back * layer) / layers && middle <= peak + (front * layer) / layers) count++;
      }
      const cover = 1 - (1 - alpha) ** count;
      red = colour[0] * cover + red * (1 - cover);
      green = colour[1] * cover + green * (1 - cover);
      blue = colour[2] * cover + blue * (1 - cover);
      opacity = cover + opacity * (1 - cover);
    }
    if (opacity < 0.002) continue;
    const style = `rgba(${Math.round(red / opacity)}, ${Math.round(green / opacity)}, ${Math.round(blue / opacity)}, ${opacity.toFixed(3)})`;
    const previous = segments[segments.length - 1];
    if (previous && previous.style === style && previous.to === from) previous.to = to;
    else segments.push({ from, to, style });
  }
  return segments;
}


/**
 * Strokes the band with its head at `progress` round the border. `length`
 * (0-1) shortens the band towards its head, keeping the colours in step.
 */
function drawBand(
  context: CanvasRenderingContext2D,
  line: Polyline,
  segments: readonly Segment[],
  progress: number,
  width: number,
  length: number
) {
  const { perimeter } = line;
  const shift = progress * perimeter;
  const place = (share: number) => ((100 - (100 - share) * length) / 100) * perimeter + shift;
  context.lineWidth = width;
  context.lineCap = "butt";
  context.lineJoin = "round";
  for (const { from, to, style } of segments) {
    context.strokeStyle = style;
    context.beginPath();
    traceRange(context, line, place(from), place(to));
    context.stroke();
  }
}

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

interface Light {
  draw: (now: number) => void;
}

// One animation frame loop for every light on the page.
const running = new Set<Light>();
let frame = 0;
function tick(now: number) {
  for (const light of running) light.draw(now);
  frame = running.size ? requestAnimationFrame(tick) : 0;
}
function start(light: Light) {
  running.add(light);
  if (!frame) frame = requestAnimationFrame(tick);
}
function stop(light: Light) {
  running.delete(light);
}

interface BorderLight {
  /** Prefix of the canvas classes, which set each layer's blur. */
  className: string;
  /** Radius of the border's centre line, half a pixel inside its outer edge. */
  radius: number;
  /** Room left round the element for the glow; the canvases cover it. */
  margin: number;
  lapSeconds: number;
  /**
   * Largest share by which each element's lap is randomly sped up or slowed
   * down, picked once when it mounts, so neighbours drift in and out of step.
   */
  lapJitter: number;
  /** Bands running round the border, spaced evenly. */
  heads: number;
  segments: readonly Segment[];
  /** Each layer is its own canvas, so a CSS filter can blur it on the GPU. */
  layers: readonly { name: string; width: number }[];
}

const COMET: BorderLight = {
  className: "comet-border",
  radius: 12.5,
  margin: 14,
  lapSeconds: 5,
  lapJitter: 0.1,
  heads: 1,
  segments: bandSegments(ARC),
  layers: [
    { name: "haze", width: 4 },
    { name: "line", width: 1.5 }
  ]
};

const LOGO_LIGHTS: BorderLight = {
  className: "logo-lights",
  radius: 5.5,
  margin: 6,
  // One lap per organization: a 1.2s cross-fade plus a 2.4s hold.
  lapSeconds: 3.6,
  lapJitter: 0,
  heads: 2,
  segments: bandSegments(ROSE_TRAIL),
  layers: [
    { name: "haze", width: 2.5 },
    { name: "line", width: 1.5 }
  ]
};

/**
 * Runs `light` round the border of the element whose canvases `wrapperRef`
 * holds. The canvases reach `margin` past the element's outer edge, and the
 * border's centre line sits half a pixel inside that edge. Each frame strokes
 * every point of a band once per layer; nothing runs while the element is off
 * screen, and reduced motion leaves it blank.
 */
function useBorderLight(
  wrapperRef: RefObject<HTMLSpanElement | null>,
  light: BorderLight,
  delaySeconds: number,
  entranceDelaySeconds: number
) {
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const canvases = [...wrapper.querySelectorAll("canvas")];
    const contexts = canvases.map((canvas) => canvas.getContext("2d"));
    if (contexts.some((context) => !context)) return;
    const { radius, margin, lapSeconds, lapJitter, heads, segments, layers } = light;
    const lap = lapSeconds * (1 + (Math.random() * 2 - 1) * lapJitter);

    let line: Polyline | null = null;
    let scale = 1;
    // When the entrance starts, in rAF time; set on the first frame drawn.
    let entranceStart: number | null = null;

    const runner: Light = {
      draw(now) {
        if (!line) return;
        entranceStart ??= now + (ENTRANCE_PAUSE_SECONDS + entranceDelaySeconds) * 1000;
        const entrance = Math.min(1, Math.max(0, (now - entranceStart) / (ENTRANCE_SECONDS * 1000)));
        const length = easeOutCubic(entrance);
        const opacity = easeOutCubic(Math.min(1, entrance * 1.6));
        const progress = (((now / 1000 - delaySeconds) / lap) % 1 + 1) % 1;
        layers.forEach(({ width }, index) => {
          const context = contexts[index] as CanvasRenderingContext2D;
          context.setTransform(1, 0, 0, 1, 0, 0);
          context.clearRect(0, 0, context.canvas.width, context.canvas.height);
          if (length === 0) return;
          context.setTransform(scale, 0, 0, scale, 0, 0);
          context.globalAlpha = opacity;
          for (let head = 0; head < heads; head++) {
            drawBand(context, line as Polyline, segments, progress + head / heads, width, length);
          }
        });
      }
    };

    const resize = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      scale = window.devicePixelRatio || 1;
      for (const canvas of canvases) {
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
      }
      line = borderPolyline(margin + 0.5, margin + 0.5, width - 2 * margin - 1, height - 2 * margin - 1, radius);
    });
    resize.observe(wrapper);

    // Only animate while the element is on screen and its section is open. One
    // callback can carry several records; the last is the current state.
    const visibility = new IntersectionObserver((entries) => {
      if (hasEnteredView(entries)) start(runner);
      else stop(runner);
    });
    visibility.observe(wrapper);

    return () => {
      stop(runner);
      resize.disconnect();
      visibility.disconnect();
    };
  }, [wrapperRef, light, delaySeconds, entranceDelaySeconds]);
}

function BorderLightCanvases({
  light,
  delaySeconds,
  entranceDelaySeconds
}: {
  light: BorderLight;
  delaySeconds: number;
  entranceDelaySeconds: number;
}) {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  useBorderLight(wrapperRef, light, delaySeconds, entranceDelaySeconds);
  return (
    <span className={light.className} aria-hidden="true" ref={wrapperRef}>
      {light.layers.map(({ name }) => (
        <canvas className={`${light.className}__${name}`} key={name} />
      ))}
    </span>
  );
}

/**
 * A soft arc of light that runs around a card's border at an even speed,
 * lavender along its tail and blue towards its front, over a blurred haze.
 * The first time it comes on screen it fades in and grows out of its head,
 * `entranceDelaySeconds` after the others so a row of cards lights up in
 * turn.
 */
export function CometBorder({
  delaySeconds,
  entranceDelaySeconds
}: {
  delaySeconds: number;
  entranceDelaySeconds: number;
}) {
  return (
    <BorderLightCanvases
      light={COMET}
      delaySeconds={delaySeconds}
      entranceDelaySeconds={entranceDelaySeconds}
    />
  );
}

/**
 * Two rose lights that circle a 26px logo's frame half a lap apart, one lap
 * per organization, for a logo that rotates. They grow in after the cards'
 * comets on the first appearance.
 */
export function LogoLights() {
  return <BorderLightCanvases light={LOGO_LIGHTS} delaySeconds={0} entranceDelaySeconds={0.6} />;
}
